import { ListApiResponse } from '../types/apiResponse';
import type {
  CompanyViewModel,
  CompanyItemModel,
  UpdateCompanyKeyProvidersModel,
} from '../types/api/companies';
import type {
  ApiListParamsWithUnderscores,
  ApiExportParams,
} from '../types/api/listParams';
import type { Stats } from '../types/stats';
import {
  apiUrl,
  getList,
  deleteRecord,
  getSingleRecord,
  postAction,
  putAction,
  getAuditData,
  get,
  exportData,
  getAll,
  getStatsData,
  upload,
} from './apiUtils';

const baseUrl = apiUrl + '/companies/';

/** Company save payload (add or update) - may have string numbers from forms */
export interface CompanySavePayload {
  id?: string;
  companyName?: string;
  transactionLimit?: string | number;
  globalTransactionLimit?: string | number;
  requestVisibleToAllUsers?: boolean;
  isProformaInvoicing?: boolean;
  outsideStockKeys?: string | number;
  companyTypeId?: string | null;
  colourHex?: string | null;
  isPublisher?: boolean;
  isDeveloper?: boolean;
  domains?: string[] | null;
  imageId?: string | null;
  users?: null;
  [key: string]: unknown;
}

export function getCompanies(
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<CompanyItemModel>> {
  return getList<CompanyItemModel>(`${baseUrl}search`, params);
}

export function getAllCompanies(): Promise<CompanyItemModel[]> {
  return getAll(baseUrl);
}

export function getRequestableCompanies(
  gameId?: string,
  skuId?: string
): Promise<unknown> {
  return skuId
    ? get(`${baseUrl}requestable?skuId=${skuId}`)
    : gameId
      ? get(`${baseUrl}requestable?gameId=${gameId}`)
      : get(`${baseUrl}requestable`);
}

export function getCompanyById(id: string): Promise<CompanyViewModel> {
  return getSingleRecord<CompanyViewModel>(baseUrl, id);
}

export function saveCompany(
  company: CompanySavePayload
): Promise<CompanyViewModel> {
  const payload: Record<string, unknown> = { ...company };
  if (payload.transactionLimit !== undefined) {
    payload.transactionLimit =
      typeof payload.transactionLimit === 'string'
        ? parseFloat((payload.transactionLimit as string).replace(/,/g, '')) ||
          0
        : payload.transactionLimit;
  }
  if (payload.globalTransactionLimit !== undefined) {
    payload.globalTransactionLimit =
      typeof payload.globalTransactionLimit === 'string'
        ? parseFloat(
            (payload.globalTransactionLimit as string).replace(/,/g, '')
          ) || 0
        : payload.globalTransactionLimit;
  }
  if (payload.outsideStockKeys !== undefined) {
    payload.outsideStockKeys =
      typeof payload.outsideStockKeys === 'string'
        ? parseInt(
            (payload.outsideStockKeys as string).replace(/,/g, ''),
            10
          ) || 0
        : payload.outsideStockKeys;
  }
  if (payload.companyTypeId === '') {
    payload.companyTypeId = null;
  }
  payload.users = null;
  return upload(baseUrl, payload) as Promise<CompanyViewModel>;
}

export function saveCompanyKeyProviders(
  id: string,
  keyProviders: UpdateCompanyKeyProvidersModel['keyProviders']
): Promise<unknown> {
  return putAction(`${baseUrl}${id}/keyproviderreferences`, {
    keyProviders,
  });
}

export function deleteCompany(id: string | number): Promise<unknown> {
  return deleteRecord(baseUrl, String(id));
}

export function getStats(id: string): Promise<Stats> {
  return getStatsData(baseUrl, id);
}

export function getStatsByDay(id: string): Promise<unknown> {
  return get(`${baseUrl}${id}/statsByDay/`);
}

export function getAggregateStats(
  id: string,
  from: Date,
  to: Date
): Promise<unknown> {
  const fromStr = from.toISOString().split('T')[0];
  const toStr = to.toISOString().split('T')[0];
  return get(
    `${baseUrl}${id}/aggregateStats?fromDate=${fromStr}&toDate=${toStr}`
  );
}

export function getAudit(id: string): Promise<unknown> {
  return getAuditData(baseUrl, id);
}

export function addAudit(id: string, comment: string): Promise<unknown> {
  return postAction(`${baseUrl}${id}/audit`, { comment });
}

export function disableKeys(id: string): Promise<unknown> {
  return postAction(`${baseUrl}${id}/disablekeys`, null);
}

export function getUsers(
  id: string,
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}${id}/users`, params);
}

export function exportCompanyListData(params: ApiExportParams): Promise<void> {
  return exportData(`${apiUrl}/export/companies/search`, 'companyName', params);
}
